/* Domain dependencies */
import { ticketModel } from '@/domain/ticket/ticket.model';

/* Application dependencies */
import { validatePersonExecute } from '@/application/ticket/validate-person.user-case';
import { listAppointmentsExecute } from '@/application/ticket/list-appointments.use-case';
import { cancelAppointmentExecute } from '@/application/ticket/cancel-appointment.use-case';
import { generateTicketExecute } from '@/application/ticket/generate-ticket.use-case';
import { listReasonsExecute } from '@/application/ticket/list-reasons.use-case';
import { typifitacionListExecute } from '@/application/common/list-typifitacionList.use-case';
import { listAttentionQueuesExecute } from '@/application/common/list-attention-queues.use-case';
import { listPrioritiesExecute } from '@/application/common/list-priorities.use-case';
import { registerNonClientExecute } from '@/application/ticket/register-non-client.use-case';

/* Infrastructure dependencies */
import { codePage, pagesTicket } from '../../enums/ticket';
import {
  apiGetCustomerBooking,
  apiUpdateBooking,
  apiGetReasonList,
  apiGetAttentionQueueList,
  apiGetPriorityList,
  apiGenerateTicket,
  apiGetTypifitacionList,
  apiValidateNonClient,
  apiRegiterNonClient,
} from '@/infrastructure/apis/attention-queues';
import { apiValidDocument } from '@/infrastructure/apis/client';
import { selectMessage } from '@/infrastructure/utils/modals';
import { API_DATA_MESSAGES } from '@/infrastructure/apis/messages/client.message';
import {
  API_GET_APPOINTMENTS_MESSAGES,
  API_SAVE_APPOINTMENT_MESSAGES,
  API_SAVE_TICKET_MESSAGES,
  API_REASONS_LIST_MESSAGES,
  API_ATTENTION_QUEUE_LIST_MESSAGES,
  API_PRIORITY_LIST_MESSAGES,
  API_TYPIFICATION_LIST_MESSAGES,
  API_REGISTER_NON_CLIENT,
} from '@/infrastructure/apis/messages/attention-queues.message';

const namespaced = true;

const state = () => ({
  step: codePage.VALIDATE_DOCUMENT,
  ticket: ticketModel,
});

const getters = {
  currentComponent: (state) => pagesTicket.find((p) => p.step === state.step),
  reasonsList: (state) => state.ticket.reasonsList,
  typificationList: (state) => state.ticket.typificationList,
  queueList: (state) => state.ticket.queueList,
  priorityList: (state) => state.ticket.priorityList,
  client: (state) => state.ticket.client,
  appointmentList: (state) => state.ticket.appointmentList,
};

const mutations = {
  SET_STEP(state, data) {
    state.step = data;
  },
  SET_DATA_CLIENT(state, data) {
    state.ticket.client = { ...state.ticket.client, ...data };
  },
  SET_REASON_LIST(state, data) {
    state.ticket.reasonsList = data;
  },
  SET_QUEUE_LIST(state, data) {
    state.ticket.queueList = data;
  },
  SET_PRIORITY_LIST(state, data) {
    state.ticket.priorityList = data;
  },
  SET_TYPIFICATION_LIST(state, data) {
    state.ticket.typificationList = data;
  },
  SET_APPOINTMENT(state, data) {
    state.ticket.appointmentList = data;
  },
  CLEAR_ALL(state) {
    state.step = codePage.VALIDATE_DOCUMENT;
    state.ticket = ticketModel;
  },
};

const actions = {
  async validatePersonAction({ commit, dispatch }, document) {
    try {
      const { dataPerson: responseValidatePerson, event } =
        await validatePersonExecute(
          document,
          apiValidDocument,
          apiValidateNonClient
        );
      if (event === 'CLIENT_FOUND') {
        const firstName = responseValidatePerson.nombre
          ? responseValidatePerson.nombre.split(' ')[0]
          : '';
        commit('SET_DATA_CLIENT', {
          document,
          fullName: `${responseValidatePerson.nombre} ${responseValidatePerson.apellidoPaterno} ${responseValidatePerson.apellidoMaterno}`,
          shortName: `${firstName} ${responseValidatePerson.apellidoPaterno}`,
          platform: responseValidatePerson.plataforma,
        });
        commit('SET_STEP', codePage.ASSIGN_TICKET);
      } else if (event === 'NON_CLIENT_REGISTERED') {
        const firstName = responseValidatePerson.name
          ? responseValidatePerson.name.split(' ')[0]
          : '';
        commit('SET_DATA_CLIENT', {
          document,
          fullName: `${responseValidatePerson.name} ${responseValidatePerson.fathersLastName} ${responseValidatePerson.mothersLastName}`,
          shortName: `${firstName} ${responseValidatePerson.fathersLastName}`,
          platform: responseValidatePerson.platformName,
        });
        commit('SET_STEP', codePage.ASSIGN_TICKET);
      } else {
        return event;
      }
    } catch (error) {
      const message = selectMessage({
        responseCode: error.errorCode,
        messageObject: API_DATA_MESSAGES,
        newMessage: error.message,
      });
      dispatch('alerts/addAlert', message, { root: true });
    }
  },
  async registerNonClientAction({ commit, dispatch }, payload) {
    try {
      const { dataNonClient } = await registerNonClientExecute(
        payload,
        apiRegiterNonClient
      );
      const firstName = dataNonClient.name
        ? dataNonClient.name.split(' ')[0]
        : '';
      commit('SET_DATA_CLIENT', {
        document: payload.document,
        fullName: `${dataNonClient.name} ${dataNonClient.fathersLastName} ${dataNonClient.mothersLastName}`,
        shortName: `${firstName} ${dataNonClient.fathersLastName}`,
        platform: dataNonClient.platformName,
      });
      commit('SET_STEP', codePage.ASSIGN_TICKET);
    } catch (error) {
      const message = selectMessage({
        responseCode: error.errorCode,
        messageObject: API_REGISTER_NON_CLIENT,
        newMessage: error.message,
      });
      dispatch('alerts/addAlert', message, { root: true });
    }
  },
  async fetchAppointmentsAction({ commit, dispatch }, document) {
    try {
      commit('SET_APPOINTMENT', []);
      const { listAppointments } = await listAppointmentsExecute(
        document,
        apiGetCustomerBooking
      );
      commit('SET_APPOINTMENT', listAppointments);
    } catch (error) {
      const message = selectMessage({
        responseCode: error.errorCode,
        messageObject: API_GET_APPOINTMENTS_MESSAGES,
        newMessage: error.message,
      });
      dispatch('alerts/addAlert', message, { root: true });
    }
  },
  async reasonsListAction({ commit, dispatch }) {
    try {
      const { listReasons } = await listReasonsExecute(apiGetReasonList);
      commit('SET_REASON_LIST', listReasons);
    } catch (error) {
      const message = selectMessage({
        responseCode: error.errorCode,
        messageObject: API_REASONS_LIST_MESSAGES,
        newMessage: error.message,
      });
      dispatch('alerts/addAlert', message, { root: true });
    }
  },
  async attentionQueueListAction({ commit, dispatch }, attentionType) {
    try {
      const { listAttentionQueues } = await listAttentionQueuesExecute(
        attentionType,
        apiGetAttentionQueueList
      );
      commit('SET_QUEUE_LIST', listAttentionQueues);
    } catch (error) {
      const message = selectMessage({
        responseCode: error.errorCode,
        messageObject: API_ATTENTION_QUEUE_LIST_MESSAGES,
        newMessage: error.message,
      });
      dispatch('alerts/addAlert', message, { root: true });
    }
  },
  async priorityListAction({ commit, dispatch }) {
    try {
      const { listPriorities } = await listPrioritiesExecute(
        apiGetPriorityList
      );
      commit('SET_PRIORITY_LIST', listPriorities);
    } catch (error) {
      const message = selectMessage({
        responseCode: error.errorCode,
        messageObject: API_PRIORITY_LIST_MESSAGES,
        newMessage: error.message,
      });
      dispatch('alerts/addAlert', message, { root: true });
    }
  },
  async cancelBookingAction({ state, dispatch }, bookingCode) {
    try {
      cancelAppointmentExecute(
        {
          bookingCode: bookingCode,
          statusCode: 4,
        },
        apiUpdateBooking
      );
      dispatch('fetchAppointmentsAction', state.ticket.client.document);
    } catch (error) {
      const message = selectMessage({
        responseCode: error.errorCode,
        messageObject: API_SAVE_APPOINTMENT_MESSAGES,
        newMessage: error.message,
      });
      dispatch('alerts/addAlert', message, { root: true });
    }
  },
  // eslint-disable-next-line no-empty-pattern
  async generateTicketAction({ dispatch }, payload) {
    try {
      await generateTicketExecute(payload, apiGenerateTicket);
      return true;
    } catch (error) {
      const message = selectMessage({
        responseCode: error.errorCode,
        messageObject: API_SAVE_TICKET_MESSAGES,
        newMessage: error.message,
      });
      dispatch('alerts/addAlert', message, { root: true });
    }
  },

  async typificationListAction({ commit, dispatch }) {
    try {
      const { typificacionList } = await typifitacionListExecute(
        apiGetTypifitacionList
      );
      commit('SET_TYPIFICATION_LIST', typificacionList);
    } catch (error) {
      const message = selectMessage({
        responseCode: error.errorCode,
        messageObject: API_TYPIFICATION_LIST_MESSAGES,
        newMessage: error.message,
      });
      dispatch('alerts/addAlert', message, { root: true });
    }
  },
};

export default {
  namespaced,
  state,
  getters,
  mutations,
  actions,
};
